import React from 'react';
import classNames from 'classnames';
import VipBadge from '@/components/AITools/VipBadge';
import { useLanguageStore } from '$/store/languageStore';
import style from './index.module.scss';

const CONTAINER_WIDTH = 288;
const PADDING = 2;
const TAB_GAP = 2;

export interface TabItem<T extends string = string> {
  key: T;
  label: string;
  /** 单个 tab 是否禁用 */
  disabled?: boolean;
  showVipBadge?: boolean;
}

interface ModeTabProps<T extends string = string> {
  tabs?: TabItem<T>[];
  activeMode: T;
  onChange?: (mode: T) => void;
  /** 为 true 时禁止切换模式 */
  disabled?: boolean;
}

/**
 * 渲染单工具模式切换 tab，默认文案跟随 smartcat 多语言配置。
 */
export default function ModeTab<T extends string = 'single' | 'batch'>({
  tabs,
  activeMode,
  onChange,
  disabled = false,
}: ModeTabProps<T>) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const resolvedTabs = tabs ?? ([
    { key: 'single', label: languageStore.smartcatTranslationText((v) => v.single_sheet_mode) },
    { key: 'batch', label: languageStore.smartcatTranslationText((v) => v.batch) },
  ] as TabItem<T>[]);
  const count = resolvedTabs.length;
  // 可用宽度 = 容器宽度 - 左右内边距 - tab之间的间距总和，再均分给每个tab
  const tabWidth = (CONTAINER_WIDTH - PADDING * 2 - (count - 1) * TAB_GAP) / count;
  const activeIndex = resolvedTabs.findIndex((tab) => tab.key === activeMode);
  // 每个tab占据 tabWidth + TAB_GAP 的步长，滑块按索引偏移
  const sliderOffset = activeIndex * (tabWidth + TAB_GAP);

  return (
    <div className={classNames(style.mode_tab_box, { [style.disabled]: disabled })}>
      <div className={style.slider} style={{ width: tabWidth, transform: `translateX(${sliderOffset}px)` }} />
      {resolvedTabs.map((tab) => (
        <div
          key={tab.key}
          className={classNames(style.tab_item, {
            [style.active_tab]: activeMode === tab.key,
            [style.tab_item_disabled]: tab.disabled,
          })}
          style={{ width: tabWidth }}
          onClick={() => {
            if (disabled || tab.disabled) return;
            onChange?.(tab.key);
          }}
        >
          <span className={style.tab_label}>{tab.label}</span>
          {tab.showVipBadge && <VipBadge className={style.vip_badge} />}
        </div>
      ))}
    </div>
  );
}
